// Der Printer: Baum zurueck in Suchtext, in der kanonischen deutschen
// Schreibweise (ST-SEA-002, „PQL Grundlage“; CM-SEA-004 „kopierbar“).
// Grundlage fuer „Als Suchtext kopieren“ und fuer den Roundtrip-Beweis
// Printer(Parser(x)) = kanonisch(x).

import { isAnd, isField, isNot, isOr, isText, type QueryNode, type QueryValue } from './baum.ts';

/** Woerter, die der Parser als Operator liest und die deshalb nur in Anfuehrungszeichen Wert sein koennen. */
const RESERVED = new Set(['ODER', 'OR', 'UND', 'AND', 'NICHT', 'NOT']);

function needsQuotes(value: string): boolean {
  return (
    value.length === 0 ||
    /[\s()":]/u.test(value) ||
    /^[-!<>]/u.test(value) ||
    value.includes('||') ||
    value.includes('&&') ||
    RESERVED.has(value)
  );
}

function quote(value: string): string {
  return `"${value.replace(/\\/gu, '\\\\').replace(/"/gu, '\\"')}"`;
}

export function printValue(value: string): string {
  return needsQuotes(value) ? quote(value) : value;
}

function printValues(values: readonly QueryValue[]): string {
  if (values.length === 1) return printValue(values[0]!.value);
  return `(${values.map((value) => printValue(value.value)).join(' ODER ')})`;
}

const COMPARATORS = { equals: '', gt: '>', gte: '>=', lt: '<', lte: '<=' } as const;

function printNode(node: QueryNode, parent: 'and' | 'or' | 'not' | 'root'): string {
  if (isText(node)) {
    // Freitext ohne Anfuehrungszeichen bleibt so, wie er gelesen wurde; eine
    // Phrase und jeder Text, der sonst anders gelesen wuerde, kommt in Zeichen.
    return node.quoted || needsTextQuotes(node.text) ? quote(node.text) : node.text;
  }
  if (isField(node)) {
    return `${node.field}:${COMPARATORS[node.op]}${printValues(node.values)}`;
  }
  if (isNot(node)) {
    return `-${printNode(node.not, 'not')}`;
  }
  if (isOr(node)) {
    const text = node.or.map((child) => printNode(child, 'or')).join(' ODER ');
    return parent === 'root' || parent === 'or' ? text : `(${text})`;
  }
  if (isAnd(node)) {
    // Zwei Freitexte nebeneinander laesen sich wieder als EIN Teilstring;
    // dort bleibt das UND ausdruecklich stehen (`-Müller UND Söhne`).
    const text = node.and
      .map((child, index) => {
        const previous = index === 0 ? undefined : node.and[index - 1];
        const separator = previous !== undefined && endsWithFreeText(previous) && startsWithFreeText(child) ? ' UND ' : ' ';
        return (index === 0 ? '' : separator) + printNode(child, 'and');
      })
      .join('');
    return parent === 'root' || parent === 'and' ? text : `(${text})`;
  }
  return '';
}

function isPlainText(node: QueryNode): boolean {
  return isText(node) && !node.quoted && !needsTextQuotes(node.text);
}

function endsWithFreeText(node: QueryNode): boolean {
  return isPlainText(node) || (isNot(node) && endsWithFreeText(node.not));
}

function startsWithFreeText(node: QueryNode): boolean {
  return isPlainText(node);
}

/** Freitext, der ohne Zeichen als Operator, Feld oder Ausschluss gelesen wuerde. */
function needsTextQuotes(text: string): boolean {
  return text.split(' ').some((word) => RESERVED.has(word) || word.includes(':') || /^[-!]./u.test(word) || /[()"]/u.test(word));
}

/** Freitext wie im Suchfeld: ohne Zeichen, solange der Parser ihn so wieder als Freitext liest. */
export function printFreeText(text: string): string {
  return needsTextQuotes(text) ? quote(text) : text;
}

export function printQuery(node: QueryNode | undefined): string {
  return node === undefined ? '' : printNode(node, 'root');
}
